import { Badge, Box, Button, Flex, HStack, Text } from '@elkaso-app/web-design';
import { useFindSimilarFamiliesApi } from 'apis/master-product-families';
import { GridCategoryModal } from 'pages/exclusive-deals/components/editable-deals-grid/grid-category-modal';
import { FamilyProposal, findNearDuplicates } from 'pages/price-imports/utils/aggregate-proposals';
import { useMemo } from 'react';

export interface FamilyProposalCardProps {
  family: FamilyProposal;
  readOnly: boolean;
  isSaving: boolean;
  onSetCategory: (family: FamilyProposal, catalogCategoryId: number) => void;
  onReviewRows: (rowNumbers: number[]) => void;
}

/**
 * One proposed family, and the two questions it raises.
 *
 * Owns its own duplicate lookup rather than the parent fetching every family
 * up front — one narrow contains-query per proposed name is both cheaper and
 * more accurate than paging the whole catalogue, and hooks cannot be called in
 * a loop, so the query has to live at card level.
 */
export const FamilyProposalCard = ({
  family,
  readOnly,
  isSaving,
  onSetCategory,
  onReviewRows,
}: FamilyProposalCardProps) => {
  const { data } = useFindSimilarFamiliesApi(family.universalName);

  const duplicates = useMemo(
    () =>
      findNearDuplicates(
        family.universalName,
        (data?.data ?? []).map((existing) => existing.universalName)
      ),
    [family.universalName, data]
  );

  const needsCategory = family.catalogCategoryId == null;

  return (
    <Box
      border='1px solid'
      borderColor={needsCategory ? 'orange.300' : 'gray.200'}
      borderRadius='8px'
      bg='white'
      px='10px'
      py='8px'>
      <Flex align='center' justify='space-between' gap='10px' wrap='wrap'>
        <Box>
          <Text fontSize='sm' fontWeight='medium'>
            {family.universalName}
          </Text>
          <Text fontSize='xs' color='gray.500'>
            {family.rowIds.length} row{family.rowIds.length === 1 ? '' : 's'}
            {family.sourceCategoryText ? ` · file says “${family.sourceCategoryText}”` : ''}
          </Text>
        </Box>

        <HStack spacing='6px'>
          {needsCategory ? (
            <GridCategoryModal
              value={null}
              onSelect={(category) => onSetCategory(family, category.id)}
              trigger={
                <Button size='xs' colorScheme='orange' isDisabled={readOnly} isLoading={isSaving}>
                  Pick a category
                </Button>
              }
            />
          ) : (
            <Badge colorScheme='green' variant='subtle'>
              Category set
            </Badge>
          )}
          <Button size='xs' variant='ghost' onClick={() => onReviewRows(family.rowNumbers)}>
            See rows
          </Button>
        </HStack>
      </Flex>

      {/* Warns, never blocks: plenty of legitimately similar families exist,
          and a guard that cries wolf gets ignored. */}
      {duplicates.length > 0 && (
        <Box mt='6px' bg='yellow.50' borderRadius='6px' px='8px' py='5px'>
          <Text fontSize='xs' color='yellow.900'>
            Already in the catalogue: {duplicates.slice(0, 3).join(', ')}. A near-duplicate family can never match the
            original again — check before approving these rows.
          </Text>
        </Box>
      )}
    </Box>
  );
};
